import type { Address, ChainId, MarketId } from '@morpho-org/blue-sdk'

import { type ChainIdAddressPointsMapping, type Point, points } from '@morpho-org/dapp-core'
import { isDefined } from '@morpho-org/morpho-ts'

import type { VaultAllocationForPoints } from '../hooks/useVaultPoints'

// Getter functions
//////// Vaults //////////
const vaultsWithPoints: ChainIdAddressPointsMapping = points.vaultsWithPoints
const vaultsWithPointsOnMarketCollateralToken: ChainIdAddressPointsMapping =
  points.vaultsWithPointsOnMarketCollateralToken
const vaultsWithPointsOnMarket: ChainIdAddressPointsMapping = points.vaultsWithPointsOnMarket

const _getVaultWithPointsTooltip = (chainId: ChainId, vaultAddress: Address) =>
  vaultsWithPoints[chainId]?.[vaultAddress] ?? []

const _getVaultWithCollateralPointsTooltip = (chainId: ChainId, collateralAddress: Address) =>
  vaultsWithPointsOnMarketCollateralToken[chainId]?.[collateralAddress] ?? []

const _getVaultWithPointsOnMarketTooltip = (chainId: ChainId, marketId: MarketId) =>
  vaultsWithPointsOnMarket[chainId]?.[marketId] ?? []

/**
 * Get all the points tooltips for a given vault
 * @param chainId - The chain id
 * @param vaultAddress - The vault address
 * @param allocation - The allocation state
 * @returns An array of points
 *  */
export const getVaultWithPoints = (
  chainId: ChainId,
  vaultAddress: Address,
  allocation: VaultAllocationForPoints[] | undefined | null
) => {
  const allocationCollateralAddresses = allocation
    ?.map(allocation => allocation.market.collateralAsset?.address)
    .filter(isDefined)

  const allocationMarketIds = allocation
    ?.filter(allocation => allocation.supplyAssetsUsd && allocation.supplyAssetsUsd > 0)
    .map(allocation => allocation.market.marketId)
    .filter(isDefined)

  const pointsTooltips: Point[] = []

  const vaultAddressWithPoints = _getVaultWithPointsTooltip(chainId, vaultAddress)

  if (vaultAddressWithPoints) {
    pointsTooltips.push(...vaultAddressWithPoints)
  }

  allocationCollateralAddresses?.forEach(address => {
    const vaultWithCollateralPoints = _getVaultWithCollateralPointsTooltip(chainId, address)
    if (vaultWithCollateralPoints) {
      pointsTooltips.push(...vaultWithCollateralPoints)
    }
  })

  allocationMarketIds?.forEach(marketId => {
    const vaultWithMarketPoints = _getVaultWithPointsOnMarketTooltip(chainId, marketId)
    if (vaultWithMarketPoints) {
      pointsTooltips.push(...vaultWithMarketPoints)
    }
  })

  return pointsTooltips
}

//////// Markets //////////
const marketsWithPoints: ChainIdAddressPointsMapping = points.marketsWithPoints
const marketsWithPointsOnCollateralToken: ChainIdAddressPointsMapping =
  points.marketsWithPointsOnCollateralToken

const _getMarketWithPointsTooltip = (chainId: ChainId, marketId: Address) =>
  marketsWithPoints[chainId]?.[marketId] ?? []

const _getMarketWithCollateralPointsTooltip = (
  chainId: ChainId,
  collateralAddress?: Address | null
) => {
  if (!collateralAddress) return []
  return marketsWithPointsOnCollateralToken[chainId]?.[collateralAddress] ?? []
}

/**
 * Points shown on lend surfaces: none.
 *
 * Every program in `marketsWithPoints` and `marketsWithPointsOnCollateralToken` is a
 * collateral-asset program (Kernel/Eigen on rsETH, Solv XP on SolvBTC, Resolv on USR),
 * earned by whoever holds the collateral. A lender holds only the loan asset, so showing
 * any of them on a lend rate would claim an incentive the lender does not accrue.
 * Point this at a real mapping if a supply-side program is ever configured.
 */
export const NO_LEND_POINTS: Point[] = []

/**
 * Get all the points tooltips for a given market
 * @param chainId - The chain id
 * @param marketId - The market id
 * @param collateralAddress - The token address
 * @returns An array of points
 */
export const getMarketWithPoints = (
  chainId: ChainId,
  marketId: MarketId,
  collateralAddress?: Address | null
) => {
  const pointsTooltips: Point[] = []

  const tokenWithPoints = _getMarketWithPointsTooltip(chainId, marketId)

  if (tokenWithPoints) {
    pointsTooltips.push(...tokenWithPoints)
  }

  const collateralWithPoints = _getMarketWithCollateralPointsTooltip(chainId, collateralAddress)

  if (collateralWithPoints) {
    pointsTooltips.push(...collateralWithPoints)
  }

  return pointsTooltips
}
